import {AbTestingUtils} from "../../../infrastructure/utils/ab-testing-utils";
import {LocalStorageUtils} from "../../../infrastructure/utils/local-storage.utils";
import {
  AD_DISPLAYS,
  ADS_PLATFORM_TYPE,
  LOCAL_STORAGE_KEYS,
  PAGES,
  ROUTE_NAMES,
  SESSION_STORAGE_KEYS,
  VIGNETTE_TYPES
} from "../../../core/consts";
import {RemoteConfigManager} from "../../../third-party-services/remote-config/remote-config-manager";
import {ExternalAdModel} from "../../../core/models/external-ad/external-ad.model";
import {APP_REMOTE_CONFIG} from "../../../core/app/app-remote-config";
import {SessionStorageUtils} from "../../../infrastructure/utils/session-storage.utils";
import type {ExternalAdLayoutModel} from "../../../core/models/external-ad/external-ad-layout.model";
import {makeObservable, observable, runInAction} from "mobx";
import {
  BaseComponentLifecycleEventHandler
} from "../../../infrastructure/handlers/base-component-lifecycle-event-handler";
import {ExternalAdDisplayEvent} from "../../../core/models/events/external-ad.display-event";
import {AnalyticsService} from "../../../third-party-services/analytics/analytics-service";
import {UserAgentViewModel} from "../../../core/models/user-agent.view-model";
import {RouterController} from "../../../core/components/router/router-controller";
import {EXTERNAL_AD_CONFIG} from "../consts/external-ad-config";

export class WebsiteModuleExternalAdDisplayHandler extends BaseComponentLifecycleEventHandler {
  localStorageUtils: LocalStorageUtils;
  abTestingUtils: AbTestingUtils;
  remoteConfigManager: RemoteConfigManager;
  @observable isAppVisible: Boolean = true;
  @observable isInitialized: Boolean = false;
  @observable isBettingPage: Boolean = true;

  static injectables = [SessionStorageUtils, AbTestingUtils, RemoteConfigManager, AnalyticsService, UserAgentViewModel, LocalStorageUtils, RouterController];

  constructor(sessionStorage: SessionStorageUtils, abTestingUtils: AbTestingUtils, remoteConfigManager: RemoteConfigManager, analyticsService: AnalyticsService, userAgentViewModel: UserAgentViewModel, localStorageUtils: LocalStorageUtils, routerController: RouterController) {
    super();

    this.isBettingPage = true;
    this.isInitialized = false;

    makeObservable(this);

    this.sessionStorage = sessionStorage;
    this.localStorageUtils = localStorageUtils;
    this.abTestingUtils = abTestingUtils;
    this.remoteConfigManager = remoteConfigManager;
    this.analyticsService = analyticsService;
    this.userAgentViewModel = userAgentViewModel;
    this.routerController = routerController;
  }

  onLoaded() {
    super.onLoaded();
    this.remoteConfigManager.registerToInitCallback(() => runInAction(() => {
      !this.isBettingPage && !this.isInitialized && this.handleModel(this.controller.config.getValue(APP_REMOTE_CONFIG.EXTERNAL_AD_CONFIG));
    }));

    this.routerController.registerToRouteChange(WebsiteModuleExternalAdDisplayHandler.name, (route) => {
      const bettingPages: Array<ROUTE_NAMES> = [ROUTE_NAMES.betting, ...(Object.values(PAGES.BETTING))];
      this.isBettingPage = bettingPages.includes(route?.config.name);

      !this.isBettingPage && !this.isInitialized && this.handleModel(this.controller.config.getValue(APP_REMOTE_CONFIG.EXTERNAL_AD_CONFIG));
    });
  }

  getABTestingResult() {
    let displayLayout = this.sessionStorage.get(SESSION_STORAGE_KEYS.externalAdLayoutDisplay);
    if (displayLayout === undefined) {
      const layoutDisplay: Array<ExternalAdLayoutModel> = this.model.remoteConfigModel.layoutsArray;
      const percentageArray = layoutDisplay.map((layout) => layout.percentage);

      let layoutIdToDisplay = this.abTestingUtils.getABTestingOption(...percentageArray);
      if (layoutIdToDisplay > -1) {
        displayLayout = layoutDisplay[layoutIdToDisplay]?.layoutType;
      }
      this.sessionStorage.set(SESSION_STORAGE_KEYS.externalAdLayoutDisplay, displayLayout);
    }

    return displayLayout;
  }

  getInterstitialDisplayResult() {
    let vignetteType = this.sessionStorage.get(SESSION_STORAGE_KEYS.vignetteTypeDisplay);
    if (vignetteType === undefined) {

      const customVignette = {type: VIGNETTE_TYPES.custom, percentage: 0};
      const vignette = {type: VIGNETTE_TYPES.google, percentage: 1 - customVignette.percentage};
      const abTesting = [customVignette, vignette];
      let layoutIdToDisplay = this.abTestingUtils.getABTestingOption(abTesting[0].percentage, abTesting[1].percentage);

      vignetteType = abTesting[layoutIdToDisplay]?.type;
      this.sessionStorage.set(SESSION_STORAGE_KEYS.vignetteTypeDisplay, vignetteType);
    }

    return vignetteType;
  }

  handleModel(model) {
    let isValid: Boolean = false;
    if (model?.layouts?.length > 0) {
      this.model.remoteConfigModel.populate(model, this.state.appState.locale.userCountryId);

      isValid = !this.userAgentViewModel.isPrerender && this.model.calcIsValid(this.state.appState.locale.userCountryId, this.state.appSettings.isWidget);
      this.model.displayLayout = this.localStorageUtils.get(LOCAL_STORAGE_KEYS.externalAdLayoutDisplay) || this.getABTestingResult();
      this.model.shouldRender = isValid;

      if (isValid) {
        if (this.model.displayLayout > 0) {
          const googleScriptSrc = "https://securepubads.g.doubleclick.net/tag/js/gpt.js";
          const adapexScriptSrc = "https://cdn.adapex.io/hb/aaw.365scores.js";
          const isAdapexAdType: Boolean = this.state.appState.adsPlatformType === ADS_PLATFORM_TYPE.adapex;
          const isGoogleAdType: Boolean = this.state.appState.adsPlatformType === ADS_PLATFORM_TYPE.google;

          const src: String | null = isAdapexAdType ? adapexScriptSrc : (isGoogleAdType ? googleScriptSrc : null);

          if (src !== null) {
            const script = document.createElement("script");
            script.src = src;
            script.async = true;

            if (isGoogleAdType) {
              script.type = "text/javascript";
              script.defer = true;
            }

            document.head.appendChild(script);
          }

          if (this.getInterstitialDisplayResult() === "GOOGLE_VIGNETTE") {
            this.initializeVignette();
          }
        }

        this.trackDisplayEvent(this.model.displayLayout)
      }

      this.isInitialized = true;
    }
  }

  trackDisplayEvent = (displayLayout) => {
    this.analyticsService.createEvent(new ExternalAdDisplayEvent({layoutType: displayLayout}));
  };

  get model(): ExternalAdModel {
    return this.state.appState.externalAdsModel;
  }

  initializeVignette() {
    const isTestAd = this.state.appState.settings.config.adDisplay === AD_DISPLAYS.test;

    window.googletag = window.googletag || {cmd: []};
    var interstitialSlot;
    window.googletag.cmd.push(function () {
      // Define a web interstitial ad slot.
      interstitialSlot = window.googletag.defineOutOfPageSlot(
        `${EXTERNAL_AD_CONFIG.VIGNETTE.adUnit}${isTestAd ? "_test" : ""}`,
        window.googletag.enums.OutOfPageFormat.INTERSTITIAL);

      // Slot returns null if the page or device does not support interstitials.
      if (interstitialSlot) {
        interstitialSlot.addService(window.googletag.pubads());
      }
      window.googletag.pubads().enableSingleRequest();
      // window.googletag.enableServices();
    });
  }
}
